import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryKeys";
import { getDomainIcon, resolveOrgUniquePreset } from "@homy/ui/icons";
import {
  Menu,
  Search,
  UserRound,
  Shield,
  ShieldAlert,
  ChevronDown,
  Check,
  ExternalLink,
  LogOut,
  Sun,
  Moon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  NotificationPopover,
} from "@homy/ui";
import { ViewSwitcher } from "@/components/views/ViewSwitcher";
import { navigatePathForItem } from "@/features/inbox/utils/inboxUtils";
import { User, Inbox, http } from "@/api";

const getOrgIcon = (orgType) => {
  if (!orgType) return getDomainIcon("organizations");
  switch (orgType) {
    case "LANDLORD":
    case "OWNER_DIRECT":
      return getDomainIcon("properties");
    case "VENDOR":
    case "CONTRACTOR":
      return getDomainIcon("maintenance");
    case "MANAGER":
    case "PROPERTY_MANAGEMENT":
    case "AGENCY":
    case "COMPANY":
    default:
      return getDomainIcon("organizations");
  }
};

export function AppHeader({
  onOpenMobileMenu,
  onOpenSearch,
  organization,
  isDarkMode,
  toggleTheme,
  user,
  onLogout,
  onNavigate,
  onExitImpersonation,
}) {
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();

  const { data: availableOrgsData } = useQuery({
    queryKey: queryKeys.organizations.available(),
    queryFn: () => User.api.listAvailableOrganizations(),
    enabled: Boolean(user),
  });

  const availableOrgs = useMemo(() => {
    if (!availableOrgsData) return [];
    if (Array.isArray(availableOrgsData)) return availableOrgsData;
    if (Array.isArray(availableOrgsData.items)) return availableOrgsData.items;
    if (Array.isArray(availableOrgsData.data)) return availableOrgsData.data;
    return [];
  }, [availableOrgsData]);

  const { data: inboxData } = useQuery({
    queryKey: queryKeys.inbox.notifications(),
    queryFn: () => Inbox.api.listItems(),
    enabled: Boolean(user),
  });

  const notificationItems = useMemo(() => {
    if (!inboxData) return [];
    const raw = Array.isArray(inboxData)
      ? inboxData
      : Array.isArray(inboxData.items)
      ? inboxData.items
      : Array.isArray(inboxData.data)
      ? inboxData.data
      : [];
    return raw.map((item) => ({
      ...item,
      id: item.id,
      title: item.title || item.subject || t("notifications", "Notification"),
      description:
        item.description || item.body || item.content || item.summary || "",
      timestamp: item.createdAt || item.created_at || item.timestamp,
      isRead: Boolean(item.read ?? item.isRead ?? item.is_read ?? false),
      read: Boolean(item.read ?? item.isRead ?? item.is_read ?? false),
      type:
        item.sourceModule ||
        item.source_module ||
        item.sourceEntityType ||
        item.source_entity_type ||
        item.itemType ||
        item.item_type ||
        item.type ||
        item.category ||
        "inbox",
    }));
  }, [inboxData, t]);

  const { data: unreadCountData } = useQuery({
    queryKey: queryKeys.inbox.unreadCount(),
    queryFn: async () => {
      const { data } = await http.get("/api/inbox/unread-count");
      return data;
    },
    enabled: Boolean(user),
  });

  const unreadInboxCount = useMemo(() => {
    if (typeof unreadCountData?.count === "number") {
      return unreadCountData.count;
    }
    return notificationItems.filter((i) => !i.isRead).length;
  }, [unreadCountData, notificationItems]);

  const handleMarkAsRead = async (itemId) => {
    try {
      await Inbox.api.updateItem(itemId, { isRead: true });
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all });
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadIds = notificationItems
        .filter((i) => !i.isRead)
        .map((i) => i.id);
      if (unreadIds.length > 0) {
        await Inbox.api.bulkUpdate({ itemIds: unreadIds, action: "READ" });
        queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all });
      }
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  };

  const handleOpenNotificationItem = (item) => {
    const targetPath = navigatePathForItem(item) || "/inbox";
    onNavigate?.(targetPath);
  };

  const handleSelectOrganization = async (orgId) => {
    if (orgId === organization?.id) return;
    try {
      await User.api.selectOrganizations({
        organizationIds: [orgId],
        defaultId: orgId,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.available() });
    } catch (err) {
      console.error("Failed to select organization:", err);
    }
  };

  const platformBaseUrl =
    import.meta.env.VITE_PLATFORM_URL ||
    (typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
      ? "http://127.0.0.1:3030"
      : "https://platform.homyos.app");

  const platformConsoleLabel = t("platformConsole", "Platform Console");

  return (
    <header className="h-14 border-b border-border/80 bg-card/90 header-fade backdrop-blur-md shrink-0 z-20 flex items-center justify-between gap-4 md:gap-8 px-3 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2 md:gap-6 flex-1 min-w-0">
        <button
          type="button"
          onClick={onOpenMobileMenu}
          className="md:hidden p-2.5 rounded-xl min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-muted-foreground hover:bg-muted/60 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("openMenu")}
        >
          <Menu size={20} />
        </button>
        <button
          type="button"
          aria-label={t("searchPlaceholder") || "Search"}
          onClick={onOpenSearch}
          className="relative md:max-w-sm w-11 h-11 md:w-full md:h-auto flex items-center justify-center md:justify-between md:pl-10 md:pr-3 py-2 bg-muted/40 border border-border hover:bg-muted/65 hover:border-muted-foreground/30 rounded-xl text-left text-sm text-muted-foreground transition-all cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/20 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0"
        >
          <Search
            className="md:absolute md:left-3 md:top-1/2 md:-translate-y-1/2 text-muted-foreground"
            size={16}
            aria-hidden
          />
          <span className="hidden md:inline">
            {t("searchPlaceholder") || "Search... (⌘K)"}
          </span>
          <kbd className="hidden md:inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-overline-size font-medium text-muted-foreground">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4">
        <ViewSwitcher className="hidden min-[480px]:inline-flex" />
        {(Boolean(user?.isPlatformAdmin) ||
          user?.role === "PLATFORM_ADMIN" ||
          user?.role === "SUPER_ADMIN" ||
          user?.roles?.includes("PLATFORM_ADMIN") ||
          user?.roles?.includes("SUPER_ADMIN")) && (
          <a
            href={platformBaseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Shield size={14} />
            <span>{platformConsoleLabel}</span>
          </a>
        )}

        {/* Notification Popover / Bell */}
        <NotificationPopover
          items={notificationItems}
          unreadCount={unreadInboxCount}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onOpenItem={handleOpenNotificationItem}
          onViewAll={() => onNavigate?.("/inbox")}
          viewAllHref="/inbox"
        />

        {/* Direct Theme Toggle for desktop viewports */}
        {toggleTheme && (
          <button
            type="button"
            onClick={toggleTheme}
            className="hidden sm:inline-flex p-2.5 rounded-xl min-h-[44px] min-w-[44px] items-center justify-center text-muted-foreground hover:bg-muted/60 transition-all border border-transparent hover:border-border cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={t("toggleTheme") || "Toggle theme"}
            aria-pressed={isDarkMode}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        )}

        {/* Unified Organization & User Top Menu (R1, R3) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex min-h-[44px] min-w-[44px] max-w-full items-center gap-2 sm:gap-3 rounded-xl",
                "border border-border/80 bg-muted/20 hover:bg-muted/50 data-[state=open]:bg-muted/50",
                "p-1 sm:px-4 sm:py-2.5",
                "transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background cursor-pointer",
              )}
              aria-label={t("userMenu.openMenu")}
            >
              <Avatar
                className={cn(
                  "h-8 w-8 sm:h-9 sm:w-9 shrink-0 border border-border transition-all",
                  user?.isImpersonated &&
                    "ring-2 ring-warning ring-offset-2 ring-offset-background shadow-xs shadow-warning/40",
                )}
              >
                <AvatarImage
                  src={
                    user?.pictureUrl ||
                    user?.picture_url ||
                    resolveOrgUniquePreset(
                      user?.id || user?.email || user?.fullName || "homy-user",
                      organization?.id || user?.organizationId || "default-org"
                    )
                  }
                  alt={user?.fullName || ""}
                  className="object-cover"
                />
                <AvatarFallback className="bg-muted">
                  <UserRound
                    className="h-4 w-4 text-muted-foreground"
                    aria-hidden
                  />
                </AvatarFallback>
              </Avatar>

              <div className="text-left hidden sm:flex flex-col min-w-0 max-w-[130px] lg:max-w-[170px]">
                <span className="text-sm font-semibold text-foreground leading-tight truncate">
                  {organization?.name || t("organization.fallback")}
                </span>
                <span className="text-xs text-muted-foreground leading-tight truncate mt-0.5">
                  {user?.fullName || t("userFallback")}
                </span>
              </div>

              <ChevronDown
                size={14}
                className="text-muted-foreground shrink-0 hidden sm:block"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 sm:w-72">
            {user?.isImpersonated && (
              <>
                <DropdownMenuItem
                  className="cursor-pointer min-h-[44px] md:min-h-9 text-warning font-semibold flex items-center justify-between bg-warning/10 focus:bg-warning/20"
                  onClick={onExitImpersonation}
                >
                  <span className="flex items-center gap-2">
                    <ShieldAlert size={14} />
                    <span>{t("impersonation.exit", "Exit Impersonation")}</span>
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}

            <div className="px-3 py-2 border-b border-border/60">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold text-foreground leading-none truncate">
                  {user?.fullName || t("userFallback")}
                </p>
                {user?.isImpersonated && (
                  <span className="px-1.5 py-0.5 rounded text-overline-size font-extrabold uppercase bg-warning/20 text-warning border border-warning/30 tracking-wider shrink-0">
                    {t("impersonation.badge", "Impersonated")}
                  </span>
                )}
              </div>
              <p className="text-overline-medium mt-1 tracking-wider text-muted-foreground truncate">
                {user?.email || user?.role || t("roleFallback")}
              </p>
            </div>

            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground px-3 pt-2 pb-1">
              {t("organization.organizations", t("organization.fallback"))}
            </DropdownMenuLabel>

            {availableOrgs.length > 0 ? (
              availableOrgs.map((org) => {
                const isSelected =
                  org.id === organization?.id ||
                  (!organization?.id && availableOrgs[0]?.id === org.id);
                const OrgIcon = getOrgIcon(org.type);
                return (
                  <DropdownMenuItem
                    key={org.id}
                    className="cursor-pointer min-h-[44px] md:min-h-9 flex items-center justify-between"
                    onClick={() => handleSelectOrganization(org.id)}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <OrgIcon
                        size={14}
                        className="text-muted-foreground shrink-0"
                      />
                      <span className="truncate">{org.name}</span>
                    </span>
                    {isSelected && (
                      <Check size={14} className="text-primary shrink-0 ml-2" />
                    )}
                  </DropdownMenuItem>
                );
              })
            ) : (
              <DropdownMenuItem
                className="cursor-pointer min-h-[44px] md:min-h-9 flex items-center justify-between"
                disabled
              >
                <span className="flex items-center gap-2 min-w-0">
                  {(() => {
                    const FallbackIcon = getOrgIcon(organization?.type);
                    return (
                      <FallbackIcon
                        size={14}
                        className="text-muted-foreground shrink-0"
                      />
                    );
                  })()}
                  <span className="truncate">
                    {organization?.name || t("organization.fallback")}
                  </span>
                </span>
                <Check size={14} className="text-primary shrink-0 ml-2" />
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              className="cursor-pointer min-h-[44px] md:min-h-9"
              onClick={() => onNavigate("/organizations")}
            >
              <span className="flex items-center gap-2">
                {React.createElement(getDomainIcon("settings"), { size: 14, className: "text-muted-foreground" })}
                <span>{t("organization.manage")}</span>
              </span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {(Boolean(user?.isPlatformAdmin) ||
              user?.role === "PLATFORM_ADMIN" ||
              user?.role === "SUPER_ADMIN" ||
              user?.roles?.includes("PLATFORM_ADMIN") ||
              user?.roles?.includes("SUPER_ADMIN")) && (
              <>
                <DropdownMenuItem
                  className="cursor-pointer min-h-[44px] md:min-h-9 text-primary font-semibold flex items-center justify-between"
                  onClick={() => window.open(platformBaseUrl, "_blank", "noopener,noreferrer")}
                >
                  <span>{platformConsoleLabel}</span>
                  <Shield size={14} className="text-primary" />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}

            <DropdownMenuItem
              className="cursor-pointer min-h-[44px] md:min-h-9"
              onClick={() => onNavigate("/settings?tab=profile")}
            >
              {t("userMenu.profile")}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer min-h-[44px] md:min-h-9"
              onClick={() => onNavigate("/settings?tab=appearance")}
            >
              {t("userMenu.appearance", "Appearance")}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer min-h-[44px] md:min-h-9"
              onClick={() => onNavigate("/trust")}
            >
              <span className="flex items-center gap-2">
                {React.createElement(getDomainIcon("trust"), { size: 14, className: "text-muted-foreground" })}
                <span>{t("nav.trust", "Trust Profile")}</span>
              </span>
            </DropdownMenuItem>
            {user?.organizationId ? (
              <DropdownMenuItem
                className="cursor-pointer min-h-[44px] md:min-h-9"
                onClick={() => onNavigate("/settings?tab=organization")}
              >
                {t("userMenu.organization")}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              className="cursor-pointer min-h-[44px] md:min-h-9"
              onClick={() => onNavigate("/settings?tab=security")}
            >
              {t("userMenu.security")}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Help Center (R3) - opens /help in a new tab */}
            <DropdownMenuItem
              asChild
              className="cursor-pointer min-h-[44px] md:min-h-9"
            >
              <a
                href="/help"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between w-full"
              >
                <span className="flex items-center gap-2">
                  {React.createElement(getDomainIcon("support"), { size: 14, className: "text-muted-foreground" })}
                  <span>{t("nav.helpCenter", "Help Center")}</span>
                </span>
                <ExternalLink size={12} className="text-muted-foreground" />
              </a>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="cursor-pointer min-h-[44px] md:min-h-9"
              onClick={onLogout}
            >
              <span className="flex items-center gap-2">
                <LogOut size={14} className="text-muted-foreground" />
                <span>{t("logout")}</span>
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
